import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Spin } from 'antd';
import toast from 'react-hot-toast';
import { loginRequest } from 'authConfig';
import { login, saveTokenToLocal, saveUserToLocal, getTokenFromLocal } from './services';
import { ROOT } from 'configs/routeNames';
import { APPLICATION_NAME } from 'configs';
import AccessDeniedCard from './AccessDeniedCard';
import { updateAbilityFromRole } from 'app/atoms';
import LoginBg from 'assets/login-bg.webp';
import LoginBgMb from 'assets/login-bg-mb.webp';

const Login = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAccessDenied, setIsAccessDenied] = useState(false);

  useEffect(() => {
    if (getTokenFromLocal()) {
      navigate(ROOT, { replace: true });
    }
  }, [navigate]);

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: (user, idToken) => {
      saveTokenToLocal(idToken);
      saveUserToLocal(user);
      queryClient.setQueryData(['auth', 'user-info'], user);
      updateAbilityFromRole(user.role);
      navigate(ROOT, { replace: true });
    },
    onError: () => setIsAccessDenied(true),
  });

  const handleLogin = async () => {
    try {
      setIsAccessDenied(false);
      const result = await instance.loginPopup(loginRequest);
      const idToken = result.idToken;
      if (!idToken) throw new Error('No ID token available after login');
      loginMutation.mutate(idToken);
    } catch {
      toast.error('Sign-in failed. Please try again.');
    }
  };

  return (
    <div
      className="relative h-[100dvh] w-screen flex flex-col items-center justify-end pb-16 sm:pb-20 px-4 overflow-hidden"
      data-testid="login-container"
    >
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat sm:hidden"
        style={{ backgroundImage: `url(${LoginBgMb})` }}
      />
      <div
        className="absolute inset-0 hidden sm:block bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${LoginBg})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

      <div className="absolute top-8 left-1/2 -translate-x-1/2 z-10 text-center">
        <h1 className="text-2xl font-semibold font-figtree text-white drop-shadow">{APPLICATION_NAME}</h1>
      </div>

      <div className="relative z-10 flex flex-col items-center gap-3 text-center">
        <p className="text-sm text-white/90 font-figtree drop-shadow">
          Sign in with your Microsoft 365 account to continue
        </p>

        {isAccessDenied ? (
          <AccessDeniedCard />
        ) : (
          <button
            onClick={handleLogin}
            disabled={loginMutation.isPending}
            className="flex items-center gap-3 bg-white border border-[#d9d9d9] rounded-xl px-6 py-3 shadow-lg hover:shadow-xl transition-shadow font-figtree font-medium text-[#000000d9] disabled:opacity-60"
            data-testid="microsoft-login-button"
          >
            {loginMutation.isPending ? (
              <Spin size="small" />
            ) : (
              <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden>
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
            )}
            Sign in with Microsoft
          </button>
        )}
      </div>
    </div>
  );
};

export default Login;
